import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Check,
  ChevronRight,
  CreditCard,
  Eye,
  LockKeyhole,
  Linkedin,
  MapPin,
  Menu,
  PackageCheck,
  Radio,
  ScanLine,
  ShieldCheck,
  ShoppingBasket,
  X,
  Zap,
} from "lucide-react";
import { Helmet } from "react-helmet-async";
import Logo from "../assets/favicon.jpeg";
import NavLogo from "../assets/nav-logo-transparent.png";
import Founder from "../assets/founder.jpeg";
import ShopperImage from "../assets/scan-as-you-shop.png";
import RetailPartnerImage from "../assets/retail-partners.png";
import AiMonitoringImage from "../assets/ai-store-monitoring.png";

interface LandingPageProps {
  onLoginClick?: () => void;
  onDeleteAccountClick?: () => void;
  onForgotPasswordClick?: () => void;
}

const AppleStoreLogo = () => (
  <svg className="lp-store-logo" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M7.078 23.55c-.473-.316-.893-.703-1.244-1.15-.383-.463-.738-.95-1.064-1.454-.766-1.12-1.365-2.345-1.78-3.636-.5-1.502-.743-2.94-.743-4.347 0-1.57.34-2.94 1.002-4.09.49-.9 1.22-1.653 2.1-2.182.85-.53 1.84-.82 2.84-.84.35 0 .73.05 1.13.15.29.08.64.21 1.07.37.55.21.85.34.95.37.32.12.59.17.8.17.16 0 .39-.05.645-.13.145-.05.42-.14.81-.31.386-.14.692-.26.935-.35.37-.11.728-.21 1.05-.26.39-.06.777-.08 1.148-.05.71.05 1.36.2 1.94.42 1.02.41 1.843 1.05 2.457 1.96-.26.16-.5.346-.725.55-.487.43-.9.94-1.23 1.505-.43.77-.65 1.64-.644 2.52.015 1.083.29 2.035.84 2.86.387.6.904 1.114 1.534 1.536.31.21.582.355.84.45-.12.375-.252.74-.405 1.1-.347.807-.76 1.58-1.25 2.31-.432.63-.772 1.1-1.03 1.41-.402.48-.79.84-1.18 1.097-.43.285-.935.436-1.452.436-.35.015-.7-.03-1.034-.127-.29-.095-.576-.202-.856-.323-.293-.134-.596-.248-.905-.34-.38-.1-.77-.148-1.164-.147-.4 0-.79.05-1.16.145-.31.088-.61.196-.907.325-.42.175-.695.29-.855.34-.324.096-.656.154-.99.175-.52 0-1.004-.15-1.486-.45zm6.854-18.46c-.68.34-1.326.484-1.973.436-.1-.646 0-1.31.27-2.037.24-.62.56-1.18 1-1.68.46-.52 1.01-.95 1.63-1.26.66-.34 1.29-.52 1.89-.55.08.68 0 1.35-.25 2.07-.228.64-.568 1.23-1 1.76-.435.52-.975.95-1.586 1.26z"
    />
  </svg>
);

const GooglePlayLogo = () => (
  <svg className="lp-store-logo" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M1.22 0c-.03.093-.06.185-.06.308v23.229c0 .217.061.34.184.463l11.415-12.093L1.22 0zm12.309 12.708 2.951 3.045-4.213 2.4s-5.355 3.044-8.308 4.739l9.57-10.184zm.801-.831 3.166 3.292c.496-.276 4.371-2.492 4.924-2.8.584-.338.525-.8.029-1.046-.459-.255-4.334-2.475-4.92-2.835l-3.203 3.392.004-.003zm-.803-.8 2.984-3.169-4.259-2.431S5.309 1.505 2.999.179l10.53 10.898h-.002z"
    />
  </svg>
);

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Manrope:wght@600;700;800&display=swap');

  .landing {
    --ink: #17211c;
    --muted: #667069;
    --cream: #f6f4ee;
    --line: #e5e5de;
    --orange: #ef5b2a;
    --orange-dark: #cf4619;
    --green: #174f3a;
    --green-light: #dcebe3;
    min-height: 100vh;
    overflow: hidden;
    background: #fff;
    color: var(--ink);
    font-family: 'DM Sans', sans-serif;
  }
  .landing * { box-sizing: border-box; }
  .landing h1,.landing h2,.landing h3,.landing p { margin: 0; }
  .landing h1,.landing h2,.landing h3 { color: var(--ink); font-family: 'Manrope', sans-serif; }
  .lp-container { width: min(1180px, calc(100% - 48px)); margin: 0 auto; }

  .lp-nav {
    position: fixed; inset: 0 0 auto; z-index: 50; height: 84px;
    background: rgba(255,255,255,.91); border-bottom: 1px solid rgba(23,33,28,.08);
    backdrop-filter: blur(18px); -webkit-backdrop-filter: blur(18px);
  }
  .lp-nav-inner { height: 100%; display: flex; align-items: center; justify-content: space-between; }
  .lp-brand { display: inline-flex; align-items: center; gap: 11px; color: var(--ink); text-decoration: none; font: 800 17px 'Manrope',sans-serif; letter-spacing: -.03em; }
  .lp-brand img { width: 40px; height: 40px; object-fit: cover; border-radius: 11px; box-shadow: 0 4px 14px rgba(23,33,28,.1); }
  .lp-brand img.lp-full-logo { width: 224px; height: 64px; object-fit: contain; border-radius: 0; box-shadow: none; }
  .lp-brand span span { color: var(--orange); }
  .lp-links { display: flex; align-items: center; gap: 32px; }
  .lp-links a { color: #505b54; text-decoration: none; font-size: 14px; font-weight: 600; transition: color .2s; }
  .lp-links a:hover { color: var(--orange); }
  .lp-nav-actions { display: flex; align-items: center; gap: 10px; }
  .lp-login { padding: 10px 16px; color: var(--ink); background: transparent; border: 0; font: 600 14px 'DM Sans',sans-serif; cursor: pointer; }
  .lp-button { min-height: 48px; display: inline-flex; align-items: center; justify-content: center; gap: 9px; padding: 0 22px; border: 0; border-radius: 12px; background: var(--orange); color: #fff; text-decoration: none; font: 700 14px 'DM Sans',sans-serif; cursor: pointer; box-shadow: 0 8px 22px rgba(239,91,42,.22); transition: transform .2s, background .2s, box-shadow .2s; }
  .lp-button:hover { transform: translateY(-2px); background: var(--orange-dark); box-shadow: 0 12px 28px rgba(239,91,42,.28); }
  .lp-button.dark { background: var(--ink); box-shadow: none; }
  .lp-button.dark:hover { background: #000; }
  .lp-menu { display: none; width: 42px; height: 42px; align-items: center; justify-content: center; border-radius: 10px; border: 1px solid var(--line); background: #fff; color: var(--ink); }
  .lp-mobile-login,
  .lp-mobile-partner { display: none; }

  .lp-hero { position: relative; padding: 154px 0 94px; background: radial-gradient(circle at 76% 30%, #fff1ea 0, transparent 32%), linear-gradient(180deg,#fff 0%,#fbfaf7 100%); }
  .lp-hero::after { content: ''; position: absolute; inset: auto 0 0; height: 1px; background: var(--line); }
  .lp-hero-grid { display: grid; grid-template-columns: 1.03fr .97fr; align-items: center; gap: 68px; }
  .lp-kicker { display: inline-flex; align-items: center; gap: 9px; margin-bottom: 24px; padding: 7px 11px; border: 1px solid #dce4de; border-radius: 999px; background: rgba(255,255,255,.8); color: var(--green); font-size: 12px; font-weight: 700; letter-spacing: .04em; text-transform: uppercase; }
  .lp-kicker i { width: 7px; height: 7px; border-radius: 50%; background: #25a461; box-shadow: 0 0 0 4px rgba(37,164,97,.12); }
  .lp-hero h1 { max-width: 650px; font-size: clamp(50px,5.5vw,76px); line-height: .99; letter-spacing: -.065em; }
  .lp-hero h1 em { color: var(--orange); font-style: normal; }
  .lp-lead { max-width: 560px; margin-top: 25px !important; color: var(--muted); font-size: 18px; line-height: 1.7; }
  .lp-hero-actions { display: flex; align-items: center; gap: 12px; margin-top: 34px; }
  .lp-downloads { display: flex; align-items: stretch; gap: 10px; }
  .lp-store-button {
    min-height: 54px; display: inline-flex; align-items: center; gap: 10px; padding: 8px 17px;
    border: 1px solid #26302b; border-radius: 12px; background: var(--ink); color: #fff;
    text-decoration: none; box-shadow: 0 8px 22px rgba(23,33,28,.14);
    transition: transform .2s, background .2s, box-shadow .2s;
  }
  .lp-store-button:hover { transform: translateY(-2px); background: #000; box-shadow: 0 12px 28px rgba(23,33,28,.2); }
  .lp-store-logo { width: 23px; height: 23px; flex: 0 0 auto; color: #fff; }
  .lp-store-button span { display: flex; flex-direction: column; line-height: 1.08; }
  .lp-store-button small { color: rgba(255,255,255,.72); font-size: 9px; font-weight: 600; letter-spacing: .02em; }
  .lp-store-button strong { margin-top: 3px; font: 700 14px 'Manrope',sans-serif; white-space: nowrap; }
  .lp-text-link { display: inline-flex; align-items: center; gap: 5px; min-height: 48px; padding: 0 14px; color: var(--ink); text-decoration: none; font-size: 14px; font-weight: 700; }
  .lp-proof { display: flex; align-items: center; gap: 18px; margin-top: 32px; color: var(--muted); font-size: 13px; }
  .lp-avatars { display: flex; }
  .lp-avatar { width: 34px; height: 34px; display: grid; place-items: center; margin-left: -7px; border: 3px solid #fff; border-radius: 50%; background: var(--green-light); color: var(--green); font-size: 10px; font-weight: 800; }
  .lp-avatar:first-child { margin-left: 0; background: #fde4d9; color: var(--orange-dark); }
  .lp-proof strong { color: var(--ink); }

  .lp-phone-stage { position: relative; min-height: 600px; display: grid; place-items: center; }
  .lp-phone-stage::before { content: ''; position: absolute; width: 470px; height: 470px; border-radius: 50%; background: #f6d9cb; }
  .lp-phone { position: relative; z-index: 2; width: 302px; padding: 10px; border-radius: 43px; background: #121916; box-shadow: 0 35px 70px rgba(23,33,28,.25), inset 0 0 0 1px rgba(255,255,255,.12); transform: rotate(2deg); }
  .lp-phone-screen { min-height: 580px; overflow: hidden; border-radius: 34px; background: #f8f8f4; }
  .lp-phone-top { height: 31px; display: flex; justify-content: center; }
  .lp-phone-pill { width: 86px; height: 22px; border-radius: 0 0 14px 14px; background: #121916; }
  .lp-phone-body { padding: 10px 18px 18px; }
  .lp-phone-greeting { display: flex; justify-content: space-between; align-items: center; }
  .lp-phone-greeting small { color: #7a827c; font-size: 10px; }
  .lp-phone-greeting strong { display: block; margin-top: 2px; font: 700 13px 'Manrope',sans-serif; }
  .lp-location { width: 30px; height: 30px; display: grid; place-items: center; border-radius: 9px; background: #fff; color: var(--orange); box-shadow: 0 2px 8px rgba(0,0,0,.06); }
  .lp-scanbox { position: relative; height: 205px; display: grid; place-items: center; margin-top: 17px; overflow: hidden; border-radius: 20px; background: linear-gradient(145deg,#26372e,#101713); color: #fff; }
  .lp-scanbox::before,.lp-scanbox::after { content: ''; position: absolute; width: 124px; height: 96px; border-color: var(--orange); border-style: solid; }
  .lp-scanbox::before { border-width: 3px 0 0 3px; border-radius: 12px 0 0; transform: translate(-22px,-12px); }
  .lp-scanbox::after { border-width: 0 3px 3px 0; border-radius: 0 0 12px; transform: translate(22px,12px); }
  .lp-scan-line { position: absolute; z-index: 2; width: 150px; height: 2px; background: var(--orange); box-shadow: 0 0 12px var(--orange); animation: lpScan 2.4s ease-in-out infinite; }
  @keyframes lpScan { 0%,100% { transform: translateY(-42px); opacity:.3 } 50% { transform: translateY(42px); opacity:1 } }
  .lp-scan-label { position: absolute; bottom: 18px; font-size: 10px; color: rgba(255,255,255,.68); }
  .lp-cart-head { display: flex; align-items: center; justify-content: space-between; margin-top: 20px; }
  .lp-cart-head strong { font: 700 13px 'Manrope',sans-serif; }
  .lp-cart-head span { color: var(--orange); font-size: 10px; font-weight: 700; }
  .lp-cart-item { display: grid; grid-template-columns: 36px 1fr auto; align-items: center; gap: 10px; margin-top: 10px; padding: 9px; border-radius: 13px; background: #fff; box-shadow: 0 3px 10px rgba(23,33,28,.05); }
  .lp-cart-icon { width: 36px; height: 36px; display: grid; place-items: center; border-radius: 10px; background: #eef2eb; color: var(--green); }
  .lp-cart-item strong { display: block; font-size: 10px; }
  .lp-cart-item small { color: #8b918d; font-size: 8px; }
  .lp-cart-price { font-size: 10px; font-weight: 800; }
  .lp-total { display: flex; justify-content: space-between; align-items: center; margin-top: 13px; padding: 13px; border-radius: 13px; background: var(--green); color: #fff; font-size: 11px; }
  .lp-total strong { font-size: 15px; }
  .lp-float-card { position: absolute; z-index: 3; display: flex; align-items: center; gap: 10px; padding: 12px 15px; border: 1px solid rgba(23,33,28,.07); border-radius: 14px; background: rgba(255,255,255,.94); box-shadow: 0 15px 40px rgba(23,33,28,.14); backdrop-filter: blur(10px); }
  .lp-float-card.one { top: 128px; right: 0; }
  .lp-float-card.two { bottom: 118px; left: 4px; }
  .lp-float-icon { width: 35px; height: 35px; display: grid; place-items: center; border-radius: 10px; background: #e0f1e7; color: #168150; }
  .lp-float-icon.orange { background: #fde5db; color: var(--orange); }
  .lp-float-card strong { display: block; font-size: 11px; }
  .lp-float-card small { color: var(--muted); font-size: 9px; }

  .lp-trust { padding: 22px 0; border-bottom: 1px solid var(--line); background: #fff; }
  .lp-trust-inner { display: flex; align-items: center; justify-content: center; gap: 44px; color: #737b75; font-size: 12px; font-weight: 600; }
  .lp-trust-item { display: inline-flex; align-items: center; gap: 8px; }

  .lp-section { padding: 112px 0; }
  .lp-section.soft { background: var(--cream); }
  .lp-section.dark { background: var(--green); color: #fff; }
  .lp-eyebrow { margin-bottom: 16px !important; color: var(--orange); font-size: 12px; font-weight: 800; letter-spacing: .12em; text-transform: uppercase; }
  .lp-section h2 { max-width: 690px; font-size: clamp(36px,4.4vw,56px); line-height: 1.08; letter-spacing: -.05em; }
  .lp-section-intro { max-width: 570px; margin-top: 20px !important; color: var(--muted); font-size: 17px; line-height: 1.65; }
  .lp-centered { text-align: center; }
  .lp-centered h2,.lp-centered .lp-section-intro { margin-left: auto !important; margin-right: auto !important; }

  .lp-steps { display: grid; grid-template-columns: repeat(3,1fr); gap: 18px; margin-top: 54px; }
  .lp-step { position: relative; min-height: 270px; padding: 28px; border: 1px solid var(--line); border-radius: 20px; background: #fff; transition: transform .25s, box-shadow .25s; }
  .lp-step:hover { transform: translateY(-5px); box-shadow: 0 18px 40px rgba(23,33,28,.07); }
  .lp-step-num { position: absolute; top: 22px; right: 25px; color: #d6d8d3; font: 800 13px 'Manrope',sans-serif; }
  .lp-step-icon { width: 52px; height: 52px; display: grid; place-items: center; margin-bottom: 36px; border-radius: 15px; background: #fde5db; color: var(--orange); }
  .lp-step h3 { margin-bottom: 11px; font-size: 19px; letter-spacing: -.03em; }
  .lp-step p { color: var(--muted); font-size: 14px; line-height: 1.65; }

  .lp-story { display: grid; grid-template-columns: 1.03fr .97fr; gap: 72px; align-items: center; }
  .lp-story-image { position: relative; min-height: 520px; overflow: hidden; border-radius: 28px; background: #ddd; }
  .lp-story-image img { position: absolute; width: 100%; height: 100%; object-fit: cover; object-position: 57% center; filter: saturate(.8) contrast(.98); }
  .lp-story-image::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg,transparent 45%,rgba(16,28,22,.58)); }
  .lp-image-note { position: absolute; z-index: 2; left: 28px; right: 28px; bottom: 26px; display: flex; align-items: flex-end; justify-content: space-between; color: #fff; }
  .lp-image-note strong { max-width: 330px; font: 700 20px/1.3 'Manrope',sans-serif; }
  .lp-image-note span { font-size: 11px; opacity: .8; }
  .lp-benefits { display: grid; gap: 19px; margin-top: 34px; }
  .lp-benefit { display: grid; grid-template-columns: 42px 1fr; gap: 15px; }
  .lp-benefit-icon { width: 42px; height: 42px; display: grid; place-items: center; border-radius: 12px; background: var(--green-light); color: var(--green); }
  .lp-benefit h3 { margin-bottom: 4px; font-size: 15px; }
  .lp-benefit p { color: var(--muted); font-size: 13px; line-height: 1.55; }

  .lp-dashboard { display: grid; grid-template-columns: .78fr 1.22fr; gap: 70px; align-items: center; }
  .lp-section.dark h2,.lp-section.dark h3 { color: #fff; }
  .lp-section.dark .lp-section-intro { color: rgba(255,255,255,.68); }
  .lp-dark-list { display: grid; gap: 14px; margin-top: 30px; }
  .lp-dark-list span { display: flex; align-items: center; gap: 10px; color: rgba(255,255,255,.82); font-size: 14px; }
  .lp-dark-list i { width: 22px; height: 22px; display: grid; place-items: center; border-radius: 50%; background: rgba(255,255,255,.12); color: #fff; }
  .lp-admin-card { padding: 25px; border: 1px solid rgba(255,255,255,.13); border-radius: 25px; background: #f8f8f4; box-shadow: 0 28px 60px rgba(3,18,11,.26); transform: rotate(1deg); }
  .lp-admin-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 24px; color: var(--ink); }
  .lp-admin-top strong { font: 700 13px 'Manrope',sans-serif; }
  .lp-live { padding: 5px 9px; border-radius: 999px; background: #e0f1e7; color: #168150; font-size: 9px; font-weight: 800; }
  .lp-metrics { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }
  .lp-metric { padding: 15px; border: 1px solid #e8e9e4; border-radius: 13px; background: #fff; }
  .lp-metric span { color: #7d857f; font-size: 8px; text-transform: uppercase; letter-spacing: .06em; }
  .lp-metric strong { display: block; margin-top: 6px; color: var(--ink); font: 800 17px 'Manrope',sans-serif; }
  .lp-chart { position: relative; height: 190px; display: flex; align-items: flex-end; gap: 14px; margin-top: 14px; padding: 25px 20px 20px; border: 1px solid #e8e9e4; border-radius: 15px; background: #fff; }
  .lp-chart::before { content:'Weekly revenue'; position:absolute; top:16px; left:20px; color:var(--ink); font:700 10px 'Manrope',sans-serif; }
  .lp-bar { flex: 1; border-radius: 5px 5px 2px 2px; background: #dcebe3; }
  .lp-bar.hot { background: var(--orange); }
  .lp-partner-photo { position: relative; min-height: 390px; margin-top: 72px; overflow: hidden; border-radius: 25px; background: #d7ded9; }
  .lp-partner-photo img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  .lp-partner-photo::after { content:''; position:absolute; inset:0; background: linear-gradient(90deg,rgba(13,37,26,.72),transparent 48%); }
  .lp-partner-caption { position: absolute; z-index: 2; top: 50%; left: 42px; max-width: 350px; transform: translateY(-50%); }
  .lp-partner-caption strong { display: block; color: #fff; font: 700 25px/1.25 'Manrope',sans-serif; letter-spacing: -.03em; }
  .lp-partner-caption span { display: block; margin-top: 10px; color: rgba(255,255,255,.7); font-size: 13px; line-height: 1.55; }

  .lp-founder { display: grid; grid-template-columns: 390px 1fr; gap: 72px; align-items: center; }
  .lp-founder-photo { position: relative; }
  .lp-founder-photo::before { content:''; position:absolute; inset: 18px -18px -18px 18px; border-radius: 25px; background:#fde5db; }
  .lp-founder-photo img { position: relative; width: 100%; aspect-ratio: 1/1.03; object-fit: cover; border-radius: 25px; filter: saturate(.88); }
  .lp-quote { max-width: 660px; margin-top: 22px !important; color: #3d4942; font: 600 clamp(22px,2.6vw,31px)/1.45 'Manrope',sans-serif; letter-spacing: -.025em; }
  .lp-founder-name { margin-top: 26px; font-size: 14px; font-weight: 800; }
  .lp-founder-role { margin-top: 3px; color: var(--muted); font-size: 12px; }

  .lp-security { background: #fff; }
  .lp-security-grid { display: grid; grid-template-columns: .92fr 1.08fr; gap: 76px; align-items: center; }
  .lp-development-tag { display: inline-flex; align-items: center; gap: 8px; margin-bottom: 17px; padding: 7px 11px; border-radius: 999px; background: #fff0ea; color: var(--orange-dark); font-size: 11px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; }
  .lp-development-tag i { width: 7px; height: 7px; border-radius: 50%; background: var(--orange); box-shadow: 0 0 0 4px rgba(239,91,42,.12); }
  .lp-security-points { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-top: 32px; }
  .lp-security-point { padding: 17px; border: 1px solid var(--line); border-radius: 15px; background: #fff; }
  .lp-security-point strong { display: block; margin-bottom: 5px; font: 700 13px 'Manrope',sans-serif; }
  .lp-security-point span { color: var(--muted); font-size: 12px; line-height: 1.5; }
  .lp-vision-panel { position: relative; min-height: 480px; overflow: hidden; border-radius: 28px; background: #111914; box-shadow: 0 28px 60px rgba(23,33,28,.18); }
  .lp-vision-image { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
  .lp-vision-panel::after { content:''; position:absolute; inset:0; background:linear-gradient(180deg,transparent 52%,rgba(8,20,14,.38)); pointer-events:none; }
  .lp-vision-grid { position: absolute; inset: 0; opacity: .18; background-image: linear-gradient(rgba(255,255,255,.09) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.09) 1px,transparent 1px); background-size: 40px 40px; }
  .lp-camera-label { position: absolute; top: 22px; left: 22px; display: inline-flex; align-items: center; gap: 8px; padding: 8px 11px; border-radius: 9px; background: rgba(255,255,255,.1); color: #fff; font-size: 10px; font-weight: 700; backdrop-filter: blur(8px); }
  .lp-camera-label i { width: 7px; height: 7px; border-radius: 50%; background: #44d17d; box-shadow: 0 0 0 4px rgba(68,209,125,.14); }
  .lp-aisle { position: absolute; inset: 92px 65px 80px; border: 1px solid rgba(255,255,255,.12); border-radius: 18px; transform: perspective(600px) rotateX(3deg); }
  .lp-shelf { position: absolute; top: 22px; bottom: 22px; width: 58px; border: 1px solid rgba(255,255,255,.13); border-radius: 8px; background: repeating-linear-gradient(180deg,rgba(255,255,255,.12) 0 4px,transparent 4px 38px); }
  .lp-shelf.left { left: 24px; } .lp-shelf.right { right: 24px; }
  .lp-person-marker { position: absolute; top: 112px; left: 48%; width: 78px; height: 146px; border: 2px solid #ef5b2a; border-radius: 12px; box-shadow: 0 0 0 1px rgba(239,91,42,.25),0 0 28px rgba(239,91,42,.17); }
  .lp-person-marker::before { content:'Movement detected'; position:absolute; top:-28px; left:-2px; width:max-content; padding:5px 7px; border-radius:5px; background:var(--orange); color:#fff; font-size:8px; font-weight:800; }
  .lp-person-shape { position: absolute; inset: 21px 19px 12px; border-radius: 50% 50% 36% 36%; background: linear-gradient(180deg,#56645c,#2a352f); }
  .lp-person-shape::before { content:''; position:absolute; top:-17px; left:12px; width:28px; height:28px; border-radius:50%; background:#69766f; }
  .lp-ai-alert { position: absolute; right: 22px; bottom: 22px; width: 250px; padding: 16px; border: 1px solid rgba(255,255,255,.13); border-radius: 15px; background: rgba(255,255,255,.09); color: #fff; backdrop-filter: blur(12px); }
  .lp-ai-alert-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 9px; }
  .lp-ai-alert-head strong { font: 700 11px 'Manrope',sans-serif; }
  .lp-ai-alert-head span { color: #ffad8f; font-size: 9px; font-weight: 800; }
  .lp-ai-alert p { color: rgba(255,255,255,.62); font-size: 10px; line-height: 1.5; }
  .lp-ai-alert-foot { display: flex; gap: 7px; margin-top: 12px; }
  .lp-ai-alert-foot span { padding: 6px 8px; border-radius: 6px; background: rgba(255,255,255,.09); color: rgba(255,255,255,.75); font-size: 8px; font-weight: 700; }
  .lp-ai-note { margin-top: 18px !important; color: #8a918c; font-size: 11px; line-height: 1.55; }

  .lp-cta { padding: 88px 0; background: #f26331; color: #fff; }
  .lp-cta-inner { display: flex; align-items: center; justify-content: space-between; gap: 50px; }
  .lp-cta h2 { max-width: 700px; color: #fff; font-size: clamp(36px,4.4vw,56px); line-height: 1.08; letter-spacing: -.05em; }
  .lp-cta p { max-width: 570px; margin-top: 14px !important; color: rgba(255,255,255,.8); font-size: 16px; }

  .lp-footer { padding: 62px 0 28px; background: #111914; color: #fff; }
  .lp-footer-grid { display: grid; grid-template-columns: 1.6fr repeat(3,1fr); gap: 50px; }
  .lp-footer .lp-brand { color: #fff; }
  .lp-footer-copy { max-width: 330px; margin-top: 18px !important; color: rgba(255,255,255,.55); font-size: 13px; line-height: 1.65; }
  .lp-footer h3 { margin-bottom: 17px; color: #fff; font-size: 12px; text-transform: uppercase; letter-spacing: .08em; }
  .lp-footer-links { display: grid; gap: 11px; }
  .lp-footer-links a { color: rgba(255,255,255,.6); text-decoration: none; font-size: 13px; }
  .lp-footer-links a:hover { color: #fff; }
  .lp-social-link { display: inline-flex; align-items: center; gap: 8px; }
  .lp-footer-bottom { display: flex; justify-content: space-between; gap: 20px; margin-top: 52px; padding-top: 22px; border-top: 1px solid rgba(255,255,255,.1); color: rgba(255,255,255,.4); font-size: 11px; }
  .lp-footer-bottom a { color: rgba(255,255,255,.75); text-decoration: none; }
  .lp-footer-bottom a:hover { color: #fff; }

  @media (max-width: 960px) {
    .lp-links { display: none; }
    .lp-menu { display: inline-flex; }
    .lp-nav-actions .lp-button { display: none; }
    .lp-mobile-open { position: fixed; inset: 76px 0 auto; display: grid; gap: 0; padding: 12px 24px 22px; background: #fff; border-bottom: 1px solid var(--line); box-shadow: 0 16px 30px rgba(23,33,28,.08); }
    .lp-mobile-open a { padding: 13px 0; border-bottom: 1px solid var(--line); }
    .lp-mobile-open .lp-mobile-login { display: flex; align-items: center; justify-content: space-between; color: var(--ink); }
    .lp-mobile-open .lp-mobile-partner { display: flex; align-items: center; justify-content: space-between; margin-top: 10px; padding: 14px 16px; border: 0; border-radius: 12px; background: var(--orange); color: #fff; }
    .lp-hero-grid,.lp-story,.lp-dashboard,.lp-founder,.lp-security-grid { grid-template-columns: 1fr; }
    .lp-hero { padding-top: 132px; }
    .lp-hero-grid { gap: 35px; }
    .lp-phone-stage { min-height: 620px; }
    .lp-story-image { min-height: 430px; order: 2; }
    .lp-dashboard { gap: 45px; }
    .lp-founder { gap: 50px; }
    .lp-security-grid { gap: 45px; }
    .lp-founder-photo { width: min(390px,90%); }
    .lp-footer-grid { grid-template-columns: 1.6fr 1fr 1fr; }
    .lp-footer-grid > div:last-child { grid-column: 2; }
  }
  @media (max-width: 700px) {
    .lp-container { width: min(100% - 32px,1180px); }
    .lp-nav { height: 72px; }
    .lp-brand { font-size: 15px; }
    .lp-brand img { width: 36px; height: 36px; }
    .lp-brand img.lp-full-logo { width: 174px; height: 52px; }
    .lp-login { display: none; }
    .lp-mobile-open { top: 72px; }
    .lp-hero { padding: 112px 0 70px; }
    .lp-hero h1 { font-size: 48px; }
    .lp-lead { font-size: 16px; }
    .lp-hero-actions { align-items: stretch; flex-direction: column; }
    .lp-downloads { width: 100%; }
    .lp-store-button { flex: 1; justify-content: center; }
    .lp-text-link { justify-content: center; }
    .lp-proof { align-items: flex-start; }
    .lp-phone-stage { min-height: 540px; transform: scale(.88); margin: -34px -25px; }
    .lp-float-card.one { right: -10px; }
    .lp-float-card.two { left: -8px; }
    .lp-trust-inner { flex-wrap: wrap; gap: 13px 22px; justify-content: flex-start; }
    .lp-section { padding: 78px 0; }
    .lp-steps { grid-template-columns: 1fr; }
    .lp-step { min-height: auto; }
    .lp-story-image { min-height: 380px; border-radius: 20px; }
    .lp-image-note { left: 20px; right: 20px; bottom: 20px; }
    .lp-image-note strong { font-size: 17px; }
    .lp-image-note span { display: none; }
    .lp-metrics { grid-template-columns: 1fr 1fr; }
    .lp-metric:last-child { grid-column: span 2; }
    .lp-admin-card { padding: 16px; }
    .lp-partner-photo { min-height: 390px; margin-top: 48px; }
    .lp-partner-photo img { object-position: 63% center; }
    .lp-partner-photo::after { background: linear-gradient(180deg,transparent 25%,rgba(13,37,26,.82)); }
    .lp-partner-caption { top: auto; right: 24px; bottom: 24px; left: 24px; transform: none; }
    .lp-founder { grid-template-columns: 1fr; }
    .lp-founder-photo { width: calc(100% - 18px); }
    .lp-security-points { grid-template-columns: 1fr; }
    .lp-vision-panel { min-height: 430px; }
    .lp-aisle { inset: 78px 24px 100px; }
    .lp-person-marker { left: 42%; }
    .lp-cta-inner { align-items: flex-start; flex-direction: column; }
    .lp-footer-grid { grid-template-columns: 1fr 1fr; }
    .lp-footer-grid > div:first-child { grid-column: span 2; }
    .lp-footer-grid > div:last-child { grid-column: auto; }
    .lp-footer-bottom { flex-direction: column; }
  }
  @media (prefers-reduced-motion: reduce) {
    .landing * { scroll-behavior: auto !important; animation: none !important; transition: none !important; }
  }
`;

const LandingPage: React.FC<LandingPageProps> = ({ onLoginClick }) => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const close = () => setMenuOpen(false);
    window.addEventListener("resize", close);
    return () => window.removeEventListener("resize", close);
  }, []);

  const login = (event: React.MouseEvent) => {
    event.preventDefault();
    onLoginClick ? onLoginClick() : navigate("/login");
  };

  const navItems = [
    ["How it works", "#how"],
    ["For shoppers", "#shoppers"],
    ["For stores", "#stores"],
    ["About", "#about"],
  ];

  return (
    <div className="landing">
      <Helmet>
        <title>JAC ScanPayGo | Scan. Pay. Go.</title>
        <meta
          name="description"
          content="JAC ScanPayGo is a mobile self-checkout platform that lets shoppers scan groceries, pay securely in-app and leave with a verified digital receipt. Designed by LBG Software for JAC ScanPayGo."
        />
        <meta name="author" content="LBG Software" />
        <meta name="designer" content="LBG Software" />
        <meta name="creator" content="LBG Software" />
        <meta property="og:title" content="JAC ScanPayGo | Scan. Pay. Go." />
        <meta
          property="og:description"
          content="Scan grocery products as you shop, pay securely in the app and leave with a verified digital receipt. Designed by LBG Software for JAC ScanPayGo."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.jacscanpaygo.co.za/" />
        <link rel="canonical" href="https://www.jacscanpaygo.co.za/" />
      </Helmet>
      <style>{styles}</style>

      <header className="lp-nav">
        <div className="lp-container lp-nav-inner">
          <a className="lp-brand" href="/" aria-label="JAC ScanPayGo home">
            <img className="lp-full-logo" src={NavLogo} alt="JAC Scan Pay Go" />
          </a>
          <nav
            className={`lp-links${menuOpen ? " lp-mobile-open" : ""}`}
            aria-label="Main navigation"
          >
            {navItems.map(([label, href]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)}>
                {label}
              </a>
            ))}
            <a
              className="lp-mobile-login"
              href="/login"
              onClick={(event) => {
                setMenuOpen(false);
                login(event);
              }}
            >
              Admin login <ChevronRight size={16} />
            </a>
            <a
              className="lp-mobile-partner"
              href="mailto:info@jacscanpaygo.co.za"
              onClick={() => setMenuOpen(false)}
            >
              Partner with us <ArrowRight size={16} />
            </a>
          </nav>
          <div className="lp-nav-actions">
            <button className="lp-login" onClick={login}>
              Admin login
            </button>
            <a className="lp-button" href="mailto:info@jacscanpaygo.co.za">
              Partner with us <ArrowRight size={15} />
            </a>
            <button
              className="lp-menu"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-label="Toggle navigation"
            >
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <main>
        <section className="lp-hero">
          <div className="lp-container lp-hero-grid">
            <div>
              <div className="lp-kicker">
                <i /> Mobile self-checkout for South African retail
              </div>
              <h1>
                Scan. Pay. Go. <em>Skip the checkout queue.</em>
              </h1>
              <p className="lp-lead">
                JAC ScanPayGo is a mobile self-checkout platform that enables
                shoppers to scan grocery products with their smartphones as
                they shop, pay securely in the app and leave the store with a
                verified digital receipt—without queueing at a traditional
                checkout.
              </p>
              <div className="lp-hero-actions" id="download">
                <div className="lp-downloads" aria-label="Download JAC ScanPayGo">
                  <a
                    className="lp-store-button"
                    href="https://apps.apple.com/za/app/jac-scanpaygo/id6765698757"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Download JAC ScanPayGo on the Apple App Store"
                  >
                    <AppleStoreLogo />
                    <span>
                      <small>Download on the</small>
                      <strong>App Store</strong>
                    </span>
                  </a>
                  <a
                    className="lp-store-button"
                    href="https://play.google.com/store/apps/details?id=com.JAC.jacandroid20&amp;pcampaignid=web_share"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Get JAC ScanPayGo on Google Play"
                  >
                    <GooglePlayLogo />
                    <span>
                      <small>Get it on</small>
                      <strong>Google Play</strong>
                    </span>
                  </a>
                </div>
                <a className="lp-text-link" href="#how">
                  See how it works <ChevronRight size={16} />
                </a>
              </div>
              <div className="lp-proof">
                <div className="lp-avatars" aria-hidden="true">
                  <span className="lp-avatar">JM</span>
                  <span className="lp-avatar">TK</span>
                  <span className="lp-avatar">LS</span>
                </div>
                <span>
                  <strong>Simple by design.</strong>
                  <br />
                  Made for shoppers of every generation.
                </span>
              </div>
            </div>

            <div
              className="lp-phone-stage"
              aria-label="Preview of the JAC ScanPayGo mobile app"
            >
              <div className="lp-float-card one">
                <span className="lp-float-icon">
                  <Check size={18} />
                </span>
                <span>
                  <strong>Item scanned</strong>
                  <small>Full Cream Milk 2L</small>
                </span>
              </div>
              <div className="lp-float-card two">
                <span className="lp-float-icon orange">
                  <CreditCard size={17} />
                </span>
                <span>
                  <strong>Payment approved</strong>
                  <small>Digital receipt ready</small>
                </span>
              </div>
              <div className="lp-phone">
                <div className="lp-phone-screen">
                  <div className="lp-phone-top">
                    <span className="lp-phone-pill" />
                  </div>
                  <div className="lp-phone-body">
                    <div className="lp-phone-greeting">
                      <div>
                        <small>Shopping at</small>
                        <strong>JAC Market, Bassonia</strong>
                      </div>
                      <span className="lp-location">
                        <MapPin size={15} />
                      </span>
                    </div>
                    <div className="lp-scanbox">
                      <ScanLine size={39} strokeWidth={1.5} />
                      <span className="lp-scan-line" />
                      <span className="lp-scan-label">
                        Align product barcode inside the frame
                      </span>
                    </div>
                    <div className="lp-cart-head">
                      <strong>Your basket</strong>
                      <span>2 items</span>
                    </div>
                    <div className="lp-cart-item">
                      <span className="lp-cart-icon">
                        <PackageCheck size={16} />
                      </span>
                      <span>
                        <strong>Full Cream Milk 2L</strong>
                        <small>Dairy · Qty 1</small>
                      </span>
                      <span className="lp-cart-price">R32.99</span>
                    </div>
                    <div className="lp-cart-item">
                      <span className="lp-cart-icon">
                        <ShoppingBasket size={16} />
                      </span>
                      <span>
                        <strong>Brown Bread 700g</strong>
                        <small>Bakery · Qty 1</small>
                      </span>
                      <span className="lp-cart-price">R18.49</span>
                    </div>
                    <div className="lp-total">
                      <span>Total</span>
                      <strong>R51.48</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="lp-trust">
          <div className="lp-container lp-trust-inner">
            <span className="lp-trust-item">
              <ShieldCheck size={17} /> Secure payments
            </span>
            <span className="lp-trust-item">
              <MapPin size={17} /> Store-verified shopping
            </span>
            <span className="lp-trust-item">
              <Zap size={17} /> Instant digital receipts
            </span>
            <span className="lp-trust-item">
              <LockKeyhole size={17} /> Protected customer data
            </span>
          </div>
        </div>

        <section className="lp-section soft" id="how">
          <div className="lp-container lp-centered">
            <p className="lp-eyebrow">How it works</p>
            <h2>From shelf to paid in three simple steps.</h2>
            <p className="lp-section-intro">
              No new shopping habits to learn. Just your phone, your basket and
              a faster way through the store.
            </p>
            <div className="lp-steps">
              {[
                {
                  icon: <MapPin size={24} />,
                  title: "Choose your store",
                  text: "Open the app and check into a participating store. Location verification keeps every shopping session accurate and secure.",
                },
                {
                  icon: <ScanLine size={24} />,
                  title: "Scan as you shop",
                  text: "Scan each product barcode before it goes into your basket. Prices and promotions appear instantly in your digital cart.",
                },
                {
                  icon: <CreditCard size={24} />,
                  title: "Pay and go",
                  text: "Review your basket, pay securely and show your verified digital receipt on your way out. No checkout queue required.",
                },
              ].map((step, index) => (
                <article className="lp-step" key={step.title}>
                  <span className="lp-step-num">0{index + 1}</span>
                  <span className="lp-step-icon">{step.icon}</span>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="lp-section" id="shoppers">
          <div className="lp-container lp-story">
            <div className="lp-story-image">
              <img
                src={ShopperImage}
                alt="A shopper scanning a grocery product with the JAC ScanPayGo experience"
              />
              <div className="lp-image-note">
                <strong>
                  Less time at checkout. More freedom to shop your way.
                </strong>
                <span>Scan as you shop</span>
              </div>
            </div>
            <div>
              <p className="lp-eyebrow">Made for everyday shopping</p>
              <h2>A better final step to every grocery run.</h2>
              <p className="lp-section-intro">
                JAC ScanPayGo removes the most frustrating part of shopping
                while keeping customers informed, secure and in control.
              </p>
              <div className="lp-benefits">
                {[
                  {
                    icon: <Zap size={19} />,
                    title: "Move faster",
                    text: "Your checkout happens while you shop, not after it.",
                  },
                  {
                    icon: <ShieldCheck size={19} />,
                    title: "Stay in control",
                    text: "See every price, quantity and promotion before you pay.",
                  },
                  {
                    icon: <PackageCheck size={19} />,
                    title: "Shop every aisle",
                    text: "Standard and weighed products are supported in one basket.",
                  },
                ].map((item) => (
                  <div className="lp-benefit" key={item.title}>
                    <span className="lp-benefit-icon">{item.icon}</span>
                    <div>
                      <h3>{item.title}</h3>
                      <p>{item.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section dark" id="stores">
          <div className="lp-container lp-dashboard">
            <div>
              <p className="lp-eyebrow">For retail partners</p>
              <h2>Smarter checkout. Clearer store operations.</h2>
              <p className="lp-section-intro">
                Give customers a modern self-checkout option while your team
                gets the controls, visibility and data needed to run with
                confidence.
              </p>
              <div className="lp-dark-list">
                {[
                  "Live revenue and transaction reporting",
                  "Product, promotion and store management",
                  "Role-based access for every branch",
                  "Receipt verification and compliance logs",
                ].map((item) => (
                  <span key={item}>
                    <i>
                      <Check size={12} />
                    </i>
                    {item}
                  </span>
                ))}
              </div>
            </div>
            <div
              className="lp-admin-card"
              aria-label="Store analytics dashboard preview"
            >
              <div className="lp-admin-top">
                <strong>Store performance</strong>
                <span className="lp-live">● LIVE</span>
              </div>
              <div className="lp-metrics">
                <div className="lp-metric">
                  <span>Today’s sales</span>
                  <strong>R24,860</strong>
                </div>
                <div className="lp-metric">
                  <span>Transactions</span>
                  <strong>184</strong>
                </div>
                <div className="lp-metric">
                  <span>Avg. basket</span>
                  <strong>R135.11</strong>
                </div>
              </div>
              <div className="lp-chart">
                {[38, 52, 46, 67, 58, 84, 73, 94, 81, 100].map(
                  (height, index) => (
                    <span
                      key={index}
                      className={`lp-bar${index === 9 ? " hot" : ""}`}
                      style={{ height: `${height}%` }}
                    />
                  ),
                )}
              </div>
            </div>
          </div>
          <div className="lp-container">
            <div className="lp-partner-photo">
              <img
                src={RetailPartnerImage}
                alt="A supermarket manager and team member reviewing store performance together"
              />
              <div className="lp-partner-caption">
                <strong>
                  Technology your floor team can use with confidence.
                </strong>
                <span>
                  Clear operational insight, practical store controls and
                  support for every branch.
                </span>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section lp-security" id="security">
          <div className="lp-container lp-security-grid">
            <div>
              <div className="lp-development-tag"><i /> In development</div>
              <p className="lp-eyebrow">AI-assisted store security</p>
              <h2>Helping store teams spot risk sooner.</h2>
              <p className="lp-section-intro">
                We are developing an intelligent monitoring system that
                analyses existing security-camera feeds for behaviour
                associated with theft, looting and unusual in-store activity—
                then brings potential incidents to trained staff for review.
              </p>
              <div className="lp-security-points">
                <div className="lp-security-point"><strong>Real-time awareness</strong><span>Surface unusual activity while store teams still have time to respond.</span></div>
                <div className="lp-security-point"><strong>Human-reviewed alerts</strong><span>AI assists decision-making; authorised staff remain in control.</span></div>
                <div className="lp-security-point"><strong>Focused monitoring</strong><span>Help teams pay attention to high-risk zones and important events.</span></div>
                <div className="lp-security-point"><strong>Responsible by design</strong><span>Built with privacy, access control and accountable use in mind.</span></div>
              </div>
              <p className="lp-ai-note">
                This capability is currently under development. Final
                functionality and deployment will be subject to testing,
                retailer policies and applicable privacy requirements.
              </p>
            </div>
            <div className="lp-vision-panel" aria-label="Concept preview of AI-assisted store monitoring">
              <img
                className="lp-vision-image"
                src={AiMonitoringImage}
                alt="Concept view of AI-assisted monitoring in a supermarket aisle"
              />
              <div className="lp-camera-label"><i /> CAM 04 · GROCERY AISLE</div>
              <div className="lp-ai-alert">
                <div className="lp-ai-alert-head">
                  <strong><Eye size={13} style={{ verticalAlign: "middle", marginRight: 6 }} />Activity alert</strong>
                  <span>REVIEW</span>
                </div>
                <p>An unusual movement pattern was detected. A store team member should review the event before taking action.</p>
                <div className="lp-ai-alert-foot">
                  <span><Radio size={9} /> Notify team</span>
                  <span>Open camera</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="lp-section soft" id="about">
          <div className="lp-container lp-founder">
            <div className="lp-founder-photo">
              <img
                src={Founder}
                alt="Kgothatso Mokomela, founder and CEO of JAC ScanPayGo"
              />
            </div>
            <div>
              <p className="lp-eyebrow">African retail, reimagined</p>
              <h2>Built from a familiar frustration.</h2>
              <p className="lp-quote">
                “We are building a checkout experience that respects people’s
                time and gives African retailers the tools to serve them
                better.”
              </p>
              <p className="lp-founder-name">Kgothatso Mokomela</p>
              <p className="lp-founder-role">
                Founder &amp; CEO, JAC ScanPayGo
              </p>
            </div>
          </div>
        </section>

        <section className="lp-cta">
          <div className="lp-container lp-cta-inner">
            <div>
              <h2>Ready to move retail forward?</h2>
              <p>
                Bring faster, mobile-first checkout to your customers and
                clearer insights to your team.
              </p>
            </div>
            <a className="lp-button dark" href="mailto:info@jacscanpaygo.co.za">
              Talk to our team <ArrowRight size={17} />
            </a>
          </div>
        </section>
      </main>

      <footer className="lp-footer">
        <div className="lp-container">
          <div className="lp-footer-grid">
            <div>
              <a className="lp-brand" href="/">
                <img src={Logo} alt="" />
                <span>
                  JAC <span>ScanPayGo</span>
                </span>
              </a>
              <p className="lp-footer-copy">
                Secure, mobile self-checkout technology designed for shoppers
                and retailers across South Africa.
              </p>
            </div>
            <div>
              <h3>Product</h3>
              <div className="lp-footer-links">
                <a href="#how">How it works</a>
                <a href="#shoppers">For shoppers</a>
                <a href="#stores">For stores</a>
                <a href="#security">AI security</a>
              </div>
            </div>
            <div>
              <h3>Company</h3>
              <div className="lp-footer-links">
                <a href="#about">About</a>
                <a href="mailto:info@jacscanpaygo.co.za">Contact</a>
                <a href="/support">Support</a>
                <a
                  className="lp-social-link"
                  href="https://www.linkedin.com/company/jac-scanpaygo/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Linkedin size={14} /> LinkedIn
                </a>
              </div>
            </div>
            <div>
              <h3>Legal</h3>
              <div className="lp-footer-links">
                <a href="/privacy">Privacy policy</a>
                <a href="/terms">Terms of service</a>
                <a href="/delete-account">Delete account</a>
              </div>
            </div>
          </div>
          <div className="lp-footer-bottom">
            <span>
              © {new Date().getFullYear()} JAC ScanPayGo. All rights reserved.
            </span>
            <span>
              Designed by{' '}
              <a href="https://www.lbgsoftware.co.za/" target="_blank" rel="noreferrer">
                LBG Software
              </a>{' '}
              for JAC ScanPayGo
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
